import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { businessAPI } from '../services/api';
import EditBusiness from './EditBusiness';
import CountrySelect from '../components/CountrySelect';
import { getPhoneCodeByCountry } from '../constants/countries';
import { OCCUPATION_CATEGORIES } from '../constants/occupations';
import { LEADERSHIP_POSITIONS } from '../constants/leadershipPositions';
import { getRegionsByCountry, getCityOptionsForAddress } from '../constants/mediterraneanRegions';
import {
  Briefcase,
  X,
  Loader2,
  CheckCircle,
  XCircle,
  Save,
  Image as ImageIcon,
  Upload,
  Trash2,
  Package,
  Clock,
  Plus,
  Users,
  Facebook,
  Twitter,
  Instagram,
  Linkedin
} from 'lucide-react';

// Helper function to generate slug from business name
const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

const CreateBusiness: React.FC = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [hasBusiness, setHasBusiness] = useState(false);
  const [myBusinessId, setMyBusinessId] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    region: '',
    description: '',
    website: '',
    email: '',
    phone: '',
    logo: '',
    address: {
      street: '',
      city: '',
      zipCode: ''
    },
    employees: 0,
    employeesAtThisAddress: 0,
    yearFounded: new Date().getFullYear(),
    rating: 0,
    products: [] as string[],
    services: [] as string[],
    openingHours: {
      monday: { open: '', close: '', closed: false },
      tuesday: { open: '', close: '', closed: false },
      wednesday: { open: '', close: '', closed: false },
      thursday: { open: '', close: '', closed: false },
      friday: { open: '', close: '', closed: false },
      saturday: { open: '', close: '', closed: false },
      sunday: { open: '', close: '', closed: false }
    },
    socialMedia: {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: ''
    },
    images: [] as string[],
    certifications: [] as Array<{
      name: string;
      issuer: string;
      date: string;
      expiryDate: string;
      certificateNumber: string;
      description: string;
    }>,
    leadership: [] as Array<{
      name: string;
      position: string;
    }>,
    // Business Information
    legalName: '',
    legalForm: '',
    registrationNumber: '',
    siretNumber: '',
    vatNumber: '',
    shareCapital: 0,
    internationalPresence: false,
    activity: '',
    subActivity: '',
    natureOfBusiness: '',
    businessActivities: '',
    // Additional fields
    termsAccepted: false,
    visibility: 'public' as 'public' | 'private'
  });
  const [productInput, setProductInput] = useState('');
  const [serviceInput, setServiceInput] = useState('');
  const [cityIsOther, setCityIsOther] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  useEffect(() => {
    checkExistingBusiness();
  }, []);

  const checkExistingBusiness = async () => {
    setChecking(true);
    try {
      const response = await businessAPI.getMyBusiness();
      if (response.data.success && response.data.data) {
        setHasBusiness(true);
        const business = response.data.data;
        setMyBusinessId(business._id || business.id || null);
      }
    } catch (err: unknown) {
      // User doesn't have a business yet, which is fine
      setHasBusiness(false);
    } finally {
      setChecking(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Handle logo: convert uploaded file to base64
      let logoValue: string | undefined = undefined;
      if (logoFile) {
        logoValue = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(logoFile);
        });
      }

      // Clean and format the data before sending
      const cleanedData: Record<string, unknown> = {
        name: formData.name.trim(),
        country: formData.country.trim() || undefined,
        region: formData.region.trim() || undefined,
        description: formData.description.trim() || undefined,
        website: formData.website.trim() || undefined,
        email: formData.email.trim(),
        phone: formData.phone.trim() || undefined,
        logo: logoValue,
        employees: formData.employees || 0,
        yearFounded: formData.yearFounded || undefined,
        rating: formData.rating || 0
      };

      // Process address
      const addressFields: Record<string, string> = {};
      if (formData.address.street.trim()) addressFields.street = formData.address.street.trim();
      if (formData.address.city.trim()) addressFields.city = formData.address.city.trim();
      if (formData.address.zipCode.trim()) addressFields.zipCode = formData.address.zipCode.trim();
      if (Object.keys(addressFields).length > 0) {
        cleanedData.address = addressFields;
      }

      if (formData.products && formData.products.length > 0) {
        cleanedData.products = formData.products.filter(p => p.trim() !== '');
      }
      if (formData.services && formData.services.length > 0) {
        cleanedData.services = formData.services.filter(s => s.trim() !== '');
      }

      // Process opening hours
      const openingHoursFields: Record<string, unknown> = {};
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      days.forEach(day => {
        const dayData = formData.openingHours[day as keyof typeof formData.openingHours];
        if (dayData && (dayData.closed || (dayData.open && dayData.close))) {
          openingHoursFields[day] = {
            open: dayData.closed ? '' : (dayData.open || ''),
            close: dayData.closed ? '' : (dayData.close || ''),
            closed: dayData.closed || false
          };
        }
      });
      if (Object.keys(openingHoursFields).length > 0) {
        cleanedData.openingHours = openingHoursFields;
      }

      // Process social media
      const socialMediaFields: Record<string, string> = {};
      if (formData.socialMedia.facebook.trim()) socialMediaFields.facebook = formData.socialMedia.facebook.trim();
      if (formData.socialMedia.twitter.trim()) socialMediaFields.twitter = formData.socialMedia.twitter.trim();
      if (formData.socialMedia.instagram.trim()) socialMediaFields.instagram = formData.socialMedia.instagram.trim();
      if (formData.socialMedia.linkedin.trim()) socialMediaFields.linkedin = formData.socialMedia.linkedin.trim();
      if (Object.keys(socialMediaFields).length > 0) {
        cleanedData.socialMedia = socialMediaFields;
      }

      if (formData.siretNumber.trim()) cleanedData.siretNumber = formData.siretNumber.trim();
      if (formData.natureOfBusiness.trim()) cleanedData.natureOfBusiness = formData.natureOfBusiness.trim();
      if (formData.businessActivities.trim()) cleanedData.businessActivities = formData.businessActivities.trim();
      if (formData.employeesAtThisAddress && formData.employeesAtThisAddress > 0) {
        cleanedData.employeesAtThisAddress = formData.employeesAtThisAddress;
      }

      // Process images
      if (imageFiles.length > 0) {
        const imagePromises = imageFiles.map(file =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          })
        );
        cleanedData.images = await Promise.all(imagePromises);
      }

      // Process certifications
      if (formData.certifications && formData.certifications.length > 0) {
        cleanedData.certifications = formData.certifications
          .filter(cert => cert.name.trim() !== '')
          .map(cert => ({
            name: cert.name.trim(),
            issuer: cert.issuer.trim() || undefined,
            date: cert.date || undefined,
            expiryDate: cert.expiryDate || undefined,
            certificateNumber: cert.certificateNumber.trim() || undefined,
            description: cert.description.trim() || undefined
          }));
      }

      // Process leadership
      if (formData.leadership && formData.leadership.length > 0) {
        const leadership = formData.leadership
          .map(l => ({ name: l.name.trim(), position: l.position.trim() }))
          .filter(l => l.name && l.position);
        if (leadership.length > 0) {
          cleanedData.leadership = leadership;
        }
      }

      if (formData.legalName.trim()) cleanedData.legalName = formData.legalName.trim();
      if (formData.legalForm) cleanedData.legalForm = formData.legalForm;
      if (formData.registrationNumber.trim()) cleanedData.registrationNumber = formData.registrationNumber.trim();
      if (formData.vatNumber.trim()) cleanedData.vatNumber = formData.vatNumber.trim();
      cleanedData.shareCapital = formData.shareCapital ?? 0;
      cleanedData.internationalPresence = formData.internationalPresence || false;
      if (formData.activity) cleanedData.activity = formData.activity;
      if (formData.subActivity) cleanedData.subActivity = formData.subActivity;
      cleanedData.termsAccepted = formData.termsAccepted;
      cleanedData.visibility = formData.visibility;

      const response = await businessAPI.createBusiness(cleanedData);

      if (response.data.success) {
        setSuccess(true);
        const createdBusiness = response.data.data;
        const slug = createdBusiness.slug || generateSlug(createdBusiness.name);
        setTimeout(() => {
          navigate(`/businesses/slug/${slug}`);
        }, 2000);
      } else {
        setError(response.data.message || 'Failed to create business');
      }
    } catch (err: unknown) {
      console.error('Error creating business:', err);
      const errObj = err as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = errObj.response?.data?.message ||
        errObj.message ||
        'Failed to create business. Please check your connection and try again.';
      setError(String(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [field]: value
        }
      }));
    } else if (name.startsWith('openingHours.')) {
      const parts = name.split('.');
      const day = parts[1];
      const field = parts[2];
      setFormData(prev => ({
        ...prev,
        openingHours: {
          ...prev.openingHours,
          [day]: {
            ...prev.openingHours[day as keyof typeof prev.openingHours],
            [field]: field === 'closed' ? (e.target as HTMLInputElement).checked : value
          }
        }
      }));
    } else if (name.startsWith('socialMedia.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        socialMedia: {
          ...prev.socialMedia,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: ['employees', 'yearFounded', 'rating', 'shareCapital', 'employeesAtThisAddress'].includes(name)
          ? (value === '' ? 0 : Number(value))
          : value
      }));
    }
  };

  const addProduct = () => {
    if (productInput.trim()) {
      setFormData(prev => ({ ...prev, products: [...prev.products, productInput.trim()] }));
      setProductInput('');
    }
  };

  const removeProduct = (index: number) => {
    setFormData(prev => ({ ...prev, products: prev.products.filter((_, i) => i !== index) }));
  };

  const addService = () => {
    if (serviceInput.trim()) {
      setFormData(prev => ({ ...prev, services: [...prev.services, serviceInput.trim()] }));
      setServiceInput('');
    }
  };

  const removeService = (index: number) => {
    setFormData(prev => ({ ...prev, services: prev.services.filter((_, i) => i !== index) }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newFiles = Array.from(files);
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

    newFiles.forEach((file) => {
      if (file.size > maxSize) {
        setError(`Image ${file.name} is too large. Maximum size is 5MB.`);
        return;
      }
      if (!allowedTypes.includes(file.type)) {
        setError(`Image ${file.name} is not a valid format. Please use JPEG, PNG, GIF, or WebP.`);
        return;
      }
      setImageFiles(prev => [...prev, file]);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeImage = (index: number) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const addCertification = () => {
    setFormData(prev => ({
      ...prev,
      certifications: [...prev.certifications, {
        name: '', issuer: '', date: '', expiryDate: '', certificateNumber: '', description: ''
      }]
    }));
  };

  const removeCertification = (index: number) => {
    setFormData(prev => ({ ...prev, certifications: prev.certifications.filter((_, i) => i !== index) }));
  };

  const updateCertification = (index: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.map((cert, i) =>
        i === index ? { ...cert, [field]: value } : cert
      )
    }));
  };

  const addLeader = () => {
    setFormData(prev => ({ ...prev, leadership: [...prev.leadership, { name: '', position: '' }] }));
  };

  const removeLeader = (index: number) => {
    setFormData(prev => ({ ...prev, leadership: prev.leadership.filter((_, i) => i !== index) }));
  };

  const updateLeader = (index: number, field: 'name' | 'position', value: string) => {
    setFormData(prev => ({
      ...prev,
      leadership: prev.leadership.map((leader, i) =>
        i === index ? { ...leader, [field]: value } : leader
      )
    }));
  };

  if (checking) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (hasBusiness && myBusinessId) {
    return <EditBusiness businessId={myBusinessId} embedInDashboard />;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <Briefcase className="h-6 w-6 text-green-600 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Create Business</h1>
              <p className="text-sm text-gray-600">Add your business to the directory</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {success && (
          <div className="mx-6 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            <p className="text-sm text-green-800">Business created successfully! Redirecting...</p>
          </div>
        )}

        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <XCircle className="h-5 w-5 text-red-600 mr-2" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Business Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Business Logo</label>
              <div className="mb-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-400 transition-colors">
                  <input
                    type="file"
                    id="logo-upload"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (!file.type.startsWith('image/')) {
                          setError('Please select an image file');
                          return;
                        }
                        if (file.size > 5 * 1024 * 1024) {
                          setError('Image size must be less than 5MB');
                          return;
                        }
                        setLogoFile(file);
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          setLogoPreview(ev.target?.result as string);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="hidden"
                  />
                  <label htmlFor="logo-upload" className="cursor-pointer flex flex-col items-center">
                    <Upload className="h-8 w-8 text-gray-400 mb-2" />
                    <span className="text-sm font-medium text-gray-700 mb-1">
                      {logoFile ? 'Change Logo' : 'Upload Logo Image'}
                    </span>
                    <span className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</span>
                  </label>
                </div>
                {logoFile && (
                  <div className="mt-3 flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-green-800">{logoFile.name}</p>
                        <p className="text-xs text-green-600">{(logoFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setLogoFile(null); setLogoPreview(null); }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
              {logoPreview && (
                <div className="mt-4">
                  <p className="text-xs text-gray-600 mb-2 font-medium">Logo Preview:</p>
                  <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50 inline-block">
                    <img src={logoPreview} alt="Logo preview" className="h-24 w-24 object-contain rounded" />
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <CountrySelect
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={(value) => {
                    setCityIsOther(false);
                    setFormData(prev => ({
                      ...prev,
                      country: value,
                      region: '',
                      address: { ...prev.address, city: '' }
                    }));
                  }}
                />
              </div>
              <div>
                <label htmlFor="region" className="block text-sm font-medium text-gray-700 mb-1">Region / State / Province</label>
                <select
                  id="region"
                  name="region"
                  value={formData.region}
                  onChange={(e) => {
                    const { value } = e.target;
                    setCityIsOther(false);
                    setFormData(prev => ({
                      ...prev,
                      region: value,
                      address: { ...prev.address, city: '' }
                    }));
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  disabled={!formData.country}
                >
                  <option value="">Select a region...</option>
                  {formData.country && getRegionsByCountry(formData.country).map((region) => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <select
                  id="city"
                  name="address.city"
                  value={
                    formData.country && formData.region
                      ? (cityIsOther || (formData.address.city && !getCityOptionsForAddress(formData.country, formData.region).includes(formData.address.city)))
                        ? '__OTHER__'
                        : formData.address.city
                      : ''
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '__OTHER__') {
                      setCityIsOther(true);
                      setFormData(prev => ({ ...prev, address: { ...prev.address, city: '' } }));
                    } else {
                      setCityIsOther(false);
                      setFormData(prev => ({ ...prev, address: { ...prev.address, city: v } }));
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  disabled={!formData.country || !formData.region}
                >
                  <option value="">{formData.country && formData.region ? 'Select city...' : 'Select country and region first'}</option>
                  {formData.country && formData.region && getCityOptionsForAddress(formData.country, formData.region).map((city) => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                  {formData.country && formData.region && <option value="__OTHER__">Other (enter below)</option>}
                </select>
                {formData.country && formData.region && (cityIsOther || (formData.address.city && !getCityOptionsForAddress(formData.country, formData.region).includes(formData.address.city))) && (
                  <input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: { ...prev.address, city: e.target.value } }))}
                    placeholder="Enter your city"
                    className="mt-2 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                )}
              </div>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                placeholder="Describe your business..."
              />
            </div>
          </div>

          {/* Legal Information */}
          <div className="space-y-4">
            <div className="border-b border-gray-200 pb-2">
              <h2 className="text-lg font-semibold text-gray-900">Legal Information</h2>
              <p className="text-xs text-gray-500 mt-1">Provide the official legal details of your company.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="legalName" className="block text-sm font-medium text-gray-700 mb-1">Legal Name</label>
                <input type="text" id="legalName" name="legalName" value={formData.legalName} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" placeholder="Enter legal name" />
              </div>
              <div>
                <label htmlFor="legalForm" className="block text-sm font-medium text-gray-700 mb-1">Legal Form</label>
                <select id="legalForm" name="legalForm" value={formData.legalForm} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent">
                  <option value="">Select legal form</option>
                  <option value="Public Establishment">Public Establishment</option>
                  <option value="Micro-enterprise">Micro-enterprise</option>
                  <option value="SME">SME</option>
                  <option value="Mid-size Company">Mid-size Company</option>
                  <option value="Large Company">Large Company</option>
                  <option value="Sole Proprietorship">Sole Proprietorship</option>
                  <option value="LLC">LLC</option>
                  <option value="Corporation">Corporation</option>
                  <option value="SAS">SAS</option>
                  <option value="SNC">SNC</option>
                  <option value="SCS">SCS</option>
                  <option value="SCA">SCA</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label htmlFor="registrationNumber" className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
                <input type="text" id="registrationNumber" name="registrationNumber" value={formData.registrationNumber} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" placeholder="Enter registration number" />
              </div>
              <div>
                <label htmlFor="siretNumber" className="block text-sm font-medium text-gray-700 mb-1">SIRET Number</label>
                <input type="text" id="siretNumber" name="siretNumber" value={formData.siretNumber} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" placeholder="Enter SIRET number" />
              </div>
              <div>
                <label htmlFor="vatNumber" className="block text-sm font-medium text-gray-700 mb-1">VAT Number</label>
                <input type="text" id="vatNumber" name="vatNumber" value={formData.vatNumber} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" placeholder="Enter VAT number" />
              </div>
              <div>
                <label htmlFor="shareCapital" className="block text-sm font-medium text-gray-700 mb-1">Share Capital</label>
                <input type="number" id="shareCapital" name="shareCapital" value={formData.shareCapital} onChange={handleChange} min="0" step="0.01" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" placeholder="Enter share capital" />
              </div>
              <div className="flex items-center">
                <input type="checkbox" id="internationalPresence" name="internationalPresence" checked={formData.internationalPresence} onChange={(e) => setFormData(prev => ({ ...prev, internationalPresence: e.target.checked }))} className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded" />
                <label htmlFor="internationalPresence" className="ml-2 block text-sm font-medium text-gray-700">International Presence</label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label htmlFor="activity" className="block text-sm font-medium text-gray-700 mb-1">Activity</label>
                <select
                  id="activity"
                  name="activity"
                  value={formData.activity}
                  onChange={(e) => setFormData(prev => ({ ...prev, activity: e.target.value, subActivity: '' }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">Select Activity...</option>
                  {OCCUPATION_CATEGORIES.map((category) => (
                    <option key={category.name} value={category.name}>{category.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="subActivity" className="block text-sm font-medium text-gray-700 mb-1">Sub-Activity</label>
                <select
                  id="subActivity"
                  name="subActivity"
                  value={formData.subActivity.startsWith('Other [') ? 'Other' : formData.subActivity}
                  onChange={(e) => {
                    const subActivity = e.target.value;
                    setFormData(prev => ({ ...prev, subActivity }));
                  }}
                  disabled={!formData.activity}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">{formData.activity ? 'Select Sub-Activity...' : 'Select Activity first'}</option>
                  {formData.activity && (() => {
                    const selectedCategory = OCCUPATION_CATEGORIES.find(cat => cat.name === formData.activity);
                    return selectedCategory?.subActivities.map((subActivity) => (
                      <option key={subActivity} value={subActivity}>{subActivity}</option>
                    ));
                  })()}
                </select>
              </div>
            </div>
            {(formData.subActivity === 'Other' || formData.subActivity.startsWith('Other [')) && (
              <div className="mt-4">
                <label htmlFor="customSubActivity" className="block text-sm font-medium text-gray-700 mb-1">Specify your sub-activity</label>
                <input
                  type="text"
                  id="customSubActivity"
                  placeholder="Enter your specific sub-activity..."
                  value={formData.subActivity.startsWith('Other [') ? formData.subActivity.match(/\[(.*?)\]/)?.[1] || '' : ''}
                  onChange={(e) => {
                    const customValue = e.target.value.trim();
                    setFormData(prev => ({ ...prev, subActivity: customValue ? `Other [${customValue}]` : 'Other' }));
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            )}
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <div className="flex">
                  <div className="flex items-center px-3 border border-r-0 border-gray-300 rounded-l-lg bg-gray-50 text-gray-700 text-sm font-medium">
                    {formData.country ? getPhoneCodeByCountry(formData.country) : '+XX'}
                  </div>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder={formData.country ? 'Enter phone number' : 'Select a country first'} disabled={!formData.country} className="flex-1 px-4 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed" />
                </div>
              </div>
              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                <input type="url" id="website" name="website" value={formData.website} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" placeholder="https://example.com" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
              <div className="space-y-2">
                <input type="text" name="address.street" value={formData.address.street} onChange={handleChange} placeholder="Street Address" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" name="address.city" value={formData.address.city} onChange={handleChange} placeholder="City" className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                  <input type="text" name="address.zipCode" value={formData.address.zipCode} onChange={handleChange} placeholder="ZIP Code" className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                </div>
              </div>
            </div>
          </div>

          {/* Products & Services */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2 flex items-center">
              <Package className="h-5 w-5 mr-2 text-green-600" />
              Products & Services
            </h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Products</label>
              <div className="flex gap-2 mb-2">
                <input type="text" value={productInput} onChange={(e) => setProductInput(e.target.value)} onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addProduct(); } }} placeholder="Enter a product name" className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                <button type="button" onClick={addProduct} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center">
                  <Plus className="h-4 w-4 mr-1" /> Add
                </button>
              </div>
              {formData.products.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.products.map((product, idx) => (
                    <span key={idx} className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                      {product}
                      <button type="button" onClick={() => removeProduct(idx)} className="ml-2 text-green-600 hover:text-green-800"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Services</label>
              <div className="flex gap-2 mb-2">
                <input type="text" value={serviceInput} onChange={(e) => setServiceInput(e.target.value)} onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addService(); } }} placeholder="Enter a service name" className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                <button type="button" onClick={addService} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center">
                  <Plus className="h-4 w-4 mr-1" /> Add
                </button>
              </div>
              {formData.services.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.services.map((service, idx) => (
                    <span key={idx} className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      {service}
                      <button type="button" onClick={() => removeService(idx)} className="ml-2 text-blue-600 hover:text-blue-800"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Opening Hours */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2 flex items-center">
              <Clock className="h-5 w-5 mr-2 text-green-600" />
              Opening Hours
            </h2>
            <div className="space-y-3">
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                const dayData = formData.openingHours[day as keyof typeof formData.openingHours];
                const dayName = day.charAt(0).toUpperCase() + day.slice(1);
                return (
                  <div key={day} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-24"><label className="text-sm font-medium text-gray-700">{dayName}</label></div>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" name={`openingHours.${day}.closed`} checked={dayData.closed} onChange={handleChange} className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded" />
                      <span className="text-sm text-gray-600">Closed</span>
                    </div>
                    {!dayData.closed && (
                      <>
                        <input type="time" name={`openingHours.${day}.open`} value={dayData.open} onChange={handleChange} className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                        <span className="text-gray-500">to</span>
                        <input type="time" name={`openingHours.${day}.close`} value={dayData.close} onChange={handleChange} className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Social Media */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2 flex items-center">
              <Facebook className="h-5 w-5 mr-2 text-green-600" />
              Social Media Links
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="socialMedia.facebook" className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><Facebook className="h-4 w-4 mr-2 text-blue-600" /> Facebook</label>
                <input type="text" id="socialMedia.facebook" name="socialMedia.facebook" value={formData.socialMedia.facebook} onChange={handleChange} placeholder="username or page name" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
              </div>
              <div>
                <label htmlFor="socialMedia.twitter" className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><Twitter className="h-4 w-4 mr-2 text-blue-400" /> Twitter</label>
                <input type="text" id="socialMedia.twitter" name="socialMedia.twitter" value={formData.socialMedia.twitter} onChange={handleChange} placeholder="@username" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
              </div>
              <div>
                <label htmlFor="socialMedia.instagram" className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><Instagram className="h-4 w-4 mr-2 text-pink-600" /> Instagram</label>
                <input type="text" id="socialMedia.instagram" name="socialMedia.instagram" value={formData.socialMedia.instagram} onChange={handleChange} placeholder="@username" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
              </div>
              <div>
                <label htmlFor="socialMedia.linkedin" className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><Linkedin className="h-4 w-4 mr-2 text-blue-700" /> LinkedIn</label>
                <input type="text" id="socialMedia.linkedin" name="socialMedia.linkedin" value={formData.socialMedia.linkedin} onChange={handleChange} placeholder="company name or username" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
              </div>
            </div>
          </div>

          {/* Photo Gallery */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2 flex items-center">
              <ImageIcon className="h-5 w-5 mr-2 text-green-600" />
              Photo Gallery
            </h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Business Photos</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-400 transition-colors">
                <input type="file" id="gallery-upload" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
                <label htmlFor="gallery-upload" className="cursor-pointer flex flex-col items-center">
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <span className="text-sm font-medium text-gray-700 mb-1">Upload Photos</span>
                  <span className="text-xs text-gray-500">PNG, JPG, GIF, WebP up to 5MB each</span>
                </label>
              </div>
              {imagePreviews.length > 0 && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                  {imagePreviews.map((preview, idx) => (
                    <div key={idx} className="relative group">
                      <img src={preview} alt={`Gallery ${idx + 1}`} className="w-full h-32 object-cover rounded-lg border-2 border-gray-200" />
                      <button type="button" onClick={() => removeImage(idx)} className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Company Leadership */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-gray-200 pb-2">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center"><Users className="h-5 w-5 mr-2 text-green-600" /> Company Leadership</h2>
              <button type="button" onClick={addLeader} className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"><Plus className="h-4 w-4 mr-1" /> Add Leader</button>
            </div>
            {formData.leadership.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <Users className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                <p>No leaders added yet. Click &quot;Add Leader&quot; to add one.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.leadership.map((leader, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-900">Leader #{idx + 1}</h3>
                      <button type="button" onClick={() => removeLeader(idx)} className="text-red-600 hover:text-red-700" aria-label="Remove leader"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <input type="text" value={leader.name} onChange={(e) => updateLeader(idx, 'name', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" placeholder="e.g. Jane Doe" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                        <select value={leader.position} onChange={(e) => updateLeader(idx, 'position', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent">
                          {(leader.position && !LEADERSHIP_POSITIONS.includes(leader.position as (typeof LEADERSHIP_POSITIONS)[number]) ? [leader.position, ...LEADERSHIP_POSITIONS] : [...LEADERSHIP_POSITIONS]).map((pos) => (
                            <option key={pos} value={pos}>{pos}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Certifications */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-gray-200 pb-2">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center"><CheckCircle className="h-5 w-5 mr-2 text-green-600" /> Certifications</h2>
              <button type="button" onClick={addCertification} className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"><Plus className="h-4 w-4 mr-1" /> Add Certification</button>
            </div>
            {formData.certifications.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p>No certifications added yet. Click &quot;Add Certification&quot; to add one.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.certifications.map((cert, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-900">Certification #{idx + 1}</h3>
                      <button type="button" onClick={() => removeCertification(idx)} className="text-red-600 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Certification Name *</label>
                        <input type="text" value={cert.name} onChange={(e) => updateCertification(idx, 'name', e.target.value)} placeholder="e.g., ISO 9001, Organic Certification" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Issuing Organization</label>
                        <input type="text" value={cert.issuer} onChange={(e) => updateCertification(idx, 'issuer', e.target.value)} placeholder="e.g., ISO, USDA" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date</label>
                        <input type="date" value={cert.date} onChange={(e) => updateCertification(idx, 'date', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                        <input type="date" value={cert.expiryDate} onChange={(e) => updateCertification(idx, 'expiryDate', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Certificate Number</label>
                        <input type="text" value={cert.certificateNumber} onChange={(e) => updateCertification(idx, 'certificateNumber', e.target.value)} placeholder="Certificate ID or number" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea value={cert.description} onChange={(e) => updateCertification(idx, 'description', e.target.value)} placeholder="Additional details about this certification" rows={2} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Business Details */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">Business Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="yearFounded" className="block text-sm font-medium text-gray-700 mb-1">Year of Establishment</label>
                <input type="number" id="yearFounded" name="yearFounded" value={formData.yearFounded} onChange={handleChange} min="1800" max={new Date().getFullYear()} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
              </div>
              <div>
                <label htmlFor="employeesAtThisAddress" className="block text-sm font-medium text-gray-700 mb-1">Number of Employees at this Address</label>
                <input type="number" id="employeesAtThisAddress" name="employeesAtThisAddress" value={formData.employeesAtThisAddress} onChange={handleChange} min="0" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
              </div>
              <div>
                <label htmlFor="employees" className="block text-sm font-medium text-gray-700 mb-1">Total Number of Employees</label>
                <input type="number" id="employees" name="employees" value={formData.employees} onChange={handleChange} min="0" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
              </div>
            </div>
          </div>

          {/* Terms and Visibility */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">Terms and Visibility</h2>
            <div className="space-y-4">
              <div className="flex items-start">
                <input type="checkbox" id="termsAccepted" name="termsAccepted" checked={formData.termsAccepted} onChange={(e) => setFormData(prev => ({ ...prev, termsAccepted: e.target.checked }))} required className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded" />
                <label htmlFor="termsAccepted" className="ml-2 block text-sm text-gray-700">I accept the terms and conditions of use *</label>
              </div>
              <div>
                <label htmlFor="visibility" className="block text-sm font-medium text-gray-700 mb-1">Visibility Status</label>
                <select id="visibility" name="visibility" value={formData.visibility} onChange={(e) => setFormData(prev => ({ ...prev, visibility: e.target.value as 'public' | 'private' }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent">
                  <option value="public">Public - Visible to everyone</option>
                  <option value="private">Private - Only visible to you</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">Public businesses can be found by anyone. Private businesses are only visible to you.</p>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-gray-200 mt-[100px]">
            <button type="button" onClick={() => navigate('/dashboard')} className="mr-3 px-4 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm">Cancel</button>
            <button type="submit" disabled={loading} className="inline-flex items-center px-4 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm">
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating...</> : <><Save className="h-4 w-4 mr-2" /> Create Business</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBusiness;
